import React, { useState, useEffect, useCallback, useRef } from 'react';
import { TETRIS_CREDIT_REWARD } from '../types/subscription';

const BOARD_WIDTH = 10;
const BOARD_HEIGHT = 20;
const CELL_SIZE = 24;

// Tetris piece shapes
const PIECES = {
  I: { shape: [[1, 1, 1, 1]], color: '#00f0f0' },
  O: { shape: [[1, 1], [1, 1]], color: '#f0f000' },
  T: { shape: [[0, 1, 0], [1, 1, 1]], color: '#a000f0' },
  S: { shape: [[0, 1, 1], [1, 1, 0]], color: '#00f000' },
  Z: { shape: [[1, 1, 0], [0, 1, 1]], color: '#f00000' },
  J: { shape: [[1, 0, 0], [1, 1, 1]], color: '#0000f0' },
  L: { shape: [[0, 0, 1], [1, 1, 1]], color: '#f0a000' },
};

type PieceType = keyof typeof PIECES;

interface Position {
  x: number;
  y: number;
}

interface CurrentPiece {
  type: PieceType;
  shape: number[][];
  position: Position;
  color: string;
}

interface TetrisGameProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (linesCleared: number) => void;
}

const createEmptyBoard = () =>
  Array.from({ length: BOARD_HEIGHT }, () => Array(BOARD_WIDTH).fill(null));

const getRandomPiece = (): CurrentPiece => {
  const types = Object.keys(PIECES) as PieceType[];
  const type = types[Math.floor(Math.random() * types.length)];
  const piece = PIECES[type];
  return {
    type,
    shape: piece.shape.map(row => [...row]),
    position: { x: Math.floor((BOARD_WIDTH - piece.shape[0].length) / 2), y: 0 },
    color: piece.color,
  };
};

const rotatePiece = (shape: number[][]): number[][] => {
  const rows = shape.length;
  const cols = shape[0].length;
  const rotated: number[][] = [];
  for (let i = 0; i < cols; i++) {
    rotated[i] = [];
    for (let j = rows - 1; j >= 0; j--) {
      rotated[i].push(shape[j][i]);
    }
  }
  return rotated;
};

const TetrisGame: React.FC<TetrisGameProps> = ({ isOpen, onClose, onComplete }) => {
  const [board, setBoard] = useState<(string | null)[][]>(createEmptyBoard());
  const [currentPiece, setCurrentPiece] = useState<CurrentPiece | null>(null);
  const [score, setScore] = useState(0);
  const [linesCleared, setLinesCleared] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const gameLoopRef = useRef<number | null>(null);

  // Target lines to clear for reward
  const TARGET_LINES = 5;

  // Check if piece can move to position
  const canMove = useCallback(
    (piece: CurrentPiece, newX: number, newY: number, newShape?: number[][]) => {
      const shape = newShape || piece.shape;
      for (let y = 0; y < shape.length; y++) {
        for (let x = 0; x < shape[y].length; x++) {
          if (shape[y][x]) {
            const boardX = newX + x;
            const boardY = newY + y;
            if (
              boardX < 0 ||
              boardX >= BOARD_WIDTH ||
              boardY >= BOARD_HEIGHT ||
              (boardY >= 0 && board[boardY][boardX])
            ) {
              return false;
            }
          }
        }
      }
      return true;
    },
    [board]
  );

  // Lock piece to board
  const lockPiece = useCallback(() => {
    if (!currentPiece) return;

    const newBoard = board.map(row => [...row]);
    for (let y = 0; y < currentPiece.shape.length; y++) {
      for (let x = 0; x < currentPiece.shape[y].length; x++) {
        if (currentPiece.shape[y][x]) {
          const boardY = currentPiece.position.y + y;
          const boardX = currentPiece.position.x + x;
          if (boardY >= 0) {
            newBoard[boardY][boardX] = currentPiece.color;
          }
        }
      }
    }

    // Check for completed lines
    let clearedLines = 0;
    for (let y = BOARD_HEIGHT - 1; y >= 0; y--) {
      if (newBoard[y].every(cell => cell !== null)) {
        newBoard.splice(y, 1);
        newBoard.unshift(Array(BOARD_WIDTH).fill(null));
        clearedLines++;
        y++; // Check same row again
      }
    }

    if (clearedLines > 0) {
      setLinesCleared(prev => prev + clearedLines);
      setScore(prev => prev + clearedLines * 100 * clearedLines);
    }

    setBoard(newBoard);

    // Spawn new piece
    const newPiece = getRandomPiece();
    if (!canMove(newPiece, newPiece.position.x, newPiece.position.y)) {
      setGameOver(true);
    } else {
      setCurrentPiece(newPiece);
    }
  }, [board, currentPiece, canMove]);

  // Move piece down
  const moveDown = useCallback(() => {
    if (!currentPiece || gameOver || isPaused) return;

    if (canMove(currentPiece, currentPiece.position.x, currentPiece.position.y + 1)) {
      setCurrentPiece({
        ...currentPiece,
        position: { ...currentPiece.position, y: currentPiece.position.y + 1 },
      });
    } else {
      lockPiece();
    }
  }, [currentPiece, gameOver, isPaused, canMove, lockPiece]);

  // Move piece left
  const moveLeft = useCallback(() => {
    if (!currentPiece || gameOver || isPaused) return;
    if (canMove(currentPiece, currentPiece.position.x - 1, currentPiece.position.y)) {
      setCurrentPiece({
        ...currentPiece,
        position: { ...currentPiece.position, x: currentPiece.position.x - 1 },
      });
    }
  }, [currentPiece, gameOver, isPaused, canMove]);

  // Move piece right
  const moveRight = useCallback(() => {
    if (!currentPiece || gameOver || isPaused) return;
    if (canMove(currentPiece, currentPiece.position.x + 1, currentPiece.position.y)) {
      setCurrentPiece({
        ...currentPiece,
        position: { ...currentPiece.position, x: currentPiece.position.x + 1 },
      });
    }
  }, [currentPiece, gameOver, isPaused, canMove]);

  // Rotate piece
  const rotate = useCallback(() => {
    if (!currentPiece || gameOver || isPaused) return;
    const rotated = rotatePiece(currentPiece.shape);
    if (canMove(currentPiece, currentPiece.position.x, currentPiece.position.y, rotated)) {
      setCurrentPiece({ ...currentPiece, shape: rotated });
    }
  }, [currentPiece, gameOver, isPaused, canMove]);

  // Drop piece
  const hardDrop = useCallback(() => {
    if (!currentPiece || gameOver || isPaused) return;
    let newY = currentPiece.position.y;
    while (canMove(currentPiece, currentPiece.position.x, newY + 1)) {
      newY++;
    }
    setCurrentPiece({
      ...currentPiece,
      position: { ...currentPiece.position, y: newY },
    });
  }, [currentPiece, gameOver, isPaused, canMove]);

  // Start game
  const startGame = useCallback(() => {
    setBoard(createEmptyBoard());
    setCurrentPiece(getRandomPiece());
    setScore(0);
    setLinesCleared(0);
    setGameOver(false);
    setGameStarted(true);
    setIsPaused(false);
  }, []);

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!gameStarted || gameOver || isPaused) return;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          moveLeft();
          break;
        case 'ArrowRight':
          e.preventDefault();
          moveRight();
          break;
        case 'ArrowDown':
          e.preventDefault();
          moveDown();
          break;
        case 'ArrowUp':
          e.preventDefault();
          rotate();
          break;
        case ' ':
          e.preventDefault();
          hardDrop();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameStarted, gameOver, isPaused, moveLeft, moveRight, moveDown, rotate, hardDrop]);

  // Game loop
  useEffect(() => {
    if (gameStarted && !gameOver && !isPaused) {
      gameLoopRef.current = window.setInterval(moveDown, 800);
    }
    return () => {
      if (gameLoopRef.current) {
        clearInterval(gameLoopRef.current);
      }
    };
  }, [gameStarted, gameOver, isPaused, moveDown]);

  // Check for win condition
  useEffect(() => {
    if (linesCleared >= TARGET_LINES && !gameOver) {
      setGameOver(true);
      onComplete(linesCleared);
    }
  }, [linesCleared, gameOver, onComplete]);

  // Reset on close
  const handleClose = () => {
    setGameStarted(false);
    setCurrentPiece(null);
    setBoard(createEmptyBoard());
    onClose();
  };

  if (!isOpen) return null;

  // Render board
  const renderBoard = () => {
    const cells = [];
    for (let y = 0; y < BOARD_HEIGHT; y++) {
      for (let x = 0; x < BOARD_WIDTH; x++) {
        let cellColor = board[y][x];

        // Draw current piece
        if (currentPiece) {
          const pieceY = y - currentPiece.position.y;
          const pieceX = x - currentPiece.position.x;
          if (
            pieceY >= 0 &&
            pieceY < currentPiece.shape.length &&
            pieceX >= 0 &&
            pieceX < currentPiece.shape[pieceY].length &&
            currentPiece.shape[pieceY][pieceX]
          ) {
            cellColor = currentPiece.color;
          }
        }

        cells.push(
          <div
            key={`${y}-${x}`}
            className="border border-gray-700"
            style={{
              width: CELL_SIZE,
              height: CELL_SIZE,
              backgroundColor: cellColor || 'transparent',
            }}
          />
        );
      }
    }
    return cells;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 max-h-[95vh] overflow-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">Tetris Challenge</h2>
          <button
            onClick={handleClose}
            className="p-2 text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Instructions or Game */}
        {!gameStarted ? (
          <div className="text-center py-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">
              Earn {TETRIS_CREDIT_REWARD} Extra Credits!
            </h3>
            <p className="text-gray-600 mb-6">
              Clear {TARGET_LINES} lines to earn bonus recording credits.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Use arrow keys to move, Up to rotate, Space to drop
            </p>
            <button
              onClick={startGame}
              className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-colors"
            >
              Start Game
            </button>
          </div>
        ) : (
          <>
            {/* Score */}
            <div className="flex justify-around mb-4">
              <div className="text-center">
                <p className="text-sm text-gray-500">Score</p>
                <p className="text-xl font-bold text-indigo-600">{score}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-500">Lines</p>
                <p className="text-xl font-bold text-emerald-600">
                  {linesCleared}/{TARGET_LINES}
                </p>
              </div>
            </div>

            {/* Game Board */}
            <div
              className="border-2 border-gray-300 rounded mx-auto relative"
              style={{
                width: BOARD_WIDTH * CELL_SIZE,
                display: 'flex',
                flexWrap: 'wrap',
              }}
            >
              {renderBoard()}

              {/* Game Over Overlay */}
              {gameOver && (
                <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center rounded">
                  <p className="text-3xl font-bold text-white mb-2">
                    {linesCleared >= TARGET_LINES ? 'You Won!' : 'Game Over'}
                  </p>
                  {linesCleared >= TARGET_LINES && (
                    <p className="text-xl text-emerald-400 font-bold mb-4">
                      +{TETRIS_CREDIT_REWARD} Credits Earned!
                    </p>
                  )}
                  <button
                    onClick={startGame}
                    className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg transition-colors"
                  >
                    Play Again
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Controls */}
            {!gameOver && (
              <div className="mt-4 md:hidden">
                <div className="flex justify-center gap-2 mb-2">
                  <button
                    onClick={moveLeft}
                    className="w-14 h-12 bg-gray-200 rounded-lg flex items-center justify-center text-xl font-bold text-gray-700"
                  >
                    ◀
                  </button>
                  <button
                    onClick={rotate}
                    className="w-14 h-12 bg-gray-200 rounded-lg flex items-center justify-center text-xl font-bold text-gray-700"
                  >
                    ↻
                  </button>
                  <button
                    onClick={moveRight}
                    className="w-14 h-12 bg-gray-200 rounded-lg flex items-center justify-center text-xl font-bold text-gray-700"
                  >
                    ▶
                  </button>
                </div>
                <div className="flex justify-center gap-2">
                  <button
                    onClick={moveDown}
                    className="w-20 h-12 bg-gray-200 rounded-lg flex items-center justify-center text-xl font-bold text-gray-700"
                  >
                    ▼
                  </button>
                  <button
                    onClick={hardDrop}
                    className="w-20 h-12 bg-indigo-600 rounded-lg flex items-center justify-center text-white font-bold"
                  >
                    Drop
                  </button>
                </div>
              </div>
            )}

            {/* Desktop Instructions */}
            {!gameOver && (
              <p className="text-center text-sm text-gray-500 mt-4 hidden md:block">
                ← → Move • ↑ Rotate • ↓ Down • Space Drop
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TetrisGame;
